/* AgentMeeting — shared UI atoms and the streaming hook */
import React from 'react';
import { MODELS, ModelGlyph, STANCE_COLOR, AgentT, MessageT, ModelId } from './data';

// ─── streaming hook ─────────────────────────────────────────
export function useStreamedText(
  msg: Pick<MessageT, 'id' | 'chunks' | 'streaming'>,
  opts: { msPerChunk?: number; autoplay?: boolean } = {}
) {
  const { msPerChunk = 320, autoplay = true } = opts;
  const full = (msg.chunks || []).join('');
  const [text, setText] = React.useState<string>(msg.streaming ? '' : full);
  const [done, setDone] = React.useState<boolean>(!msg.streaming);

  React.useEffect(() => {
    if (!msg.streaming || !autoplay) {
      setText(full);
      setDone(true);
      return;
    }
    let i = 0;
    let acc = '';
    setText('');
    setDone(false);
    let to: ReturnType<typeof setTimeout>;
    const tick = () => {
      if (i >= msg.chunks.length) { setDone(true); return; }
      acc += msg.chunks[i];
      setText(acc);
      i++;
      to = setTimeout(tick, msPerChunk + Math.random() * 220);
    };
    to = setTimeout(tick, 200);
    return () => clearTimeout(to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msg.id, msg.streaming, autoplay]);

  return { text, done };
}

// ─── Avatar ────────────────────────────────────────────────
type Stance = keyof typeof STANCE_COLOR;
export function Avatar({ agent, size }: { agent?: AgentT | null; size?: 'sm' | 'lg' }) {
  if (!agent) return null;
  const stance = (agent.stance || 'neutral') as Stance;
  const c = STANCE_COLOR[stance] || 'var(--fg-2)';
  const short = agent.short || '··';
  const cls = 'av' + (size === 'lg' ? ' lg' : size === 'sm' ? ' sm' : '');
  return <div className={cls} style={{ background: c }}>{short}</div>;
}

// ─── Stance tag ────────────────────────────────────────────
export function StanceTag({ agent, withDot = true }: { agent?: AgentT | null; withDot?: boolean }) {
  if (!agent || !agent.stance) return null;
  const c = STANCE_COLOR[agent.stance] || 'var(--fg-2)';
  const label = agent.stanceLabel || agent.stance;
  return (
    <span className="tag" style={{ borderColor: 'transparent', color: c, background: 'transparent', padding: '2px 0' }}>
      {withDot && <span className="dot" style={{ background: c }} />}
      <span style={{ letterSpacing: '0.04em' }}>{label}</span>
    </span>
  );
}

// ─── Model chip ────────────────────────────────────────────
export function ModelChip({
  modelId, withCost, costCents,
}: { modelId?: ModelId | null; withCost?: boolean; costCents?: number }) {
  if (!modelId) return null;
  const m = MODELS[modelId];
  if (!m) return null;
  return (
    <span className="model-chip">
      <ModelGlyph id={modelId} size={11} />
      <span>{m.short}</span>
      {withCost && costCents != null && (
        <span className="dim" style={{ marginLeft: 4 }}>
          ${(costCents / 100).toFixed(2)}
        </span>
      )}
    </span>
  );
}

// ─── Round counter ─────────────────────────────────────────
export function RoundSteps({ current, total }: { current: number; total: number }) {
  return (
    <div className="steps">
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className={'step ' + (i < current ? 'done' : i === current ? 'cur' : '')} />
      ))}
      <span className="mono dim" style={{ marginLeft: 6 }}>r{current + 1}/{total}</span>
    </div>
  );
}

// ─── Token / cost meter ────────────────────────────────────
export function CostMeter({ tokens, cents, budget = 100 }: { tokens: number; cents: number; budget?: number }) {
  const pct = Math.min(100, (cents / budget) * 100);
  return (
    <div className="flex center gap-2 mono tiny">
      <span className="dim">tok</span>
      <span className="tnum">{(tokens / 1000).toFixed(1)}k</span>
      <span className="dim">·</span>
      <span className="dim">spent</span>
      <span className="tnum">${(cents / 100).toFixed(2)}</span>
      <div style={{ width: 56, height: 4, background: 'var(--bg-3)', borderRadius: 1, position: 'relative' }}>
        <div style={{
          position: 'absolute', inset: 0, width: pct + '%',
          background: pct > 80 ? 'var(--err)' : 'var(--live)',
          borderRadius: 1,
        }} />
      </div>
      <span className="dim">/${budget / 100}</span>
    </div>
  );
}

// ─── Inline @mention rendering ─────────────────────────────
export function renderInline(text?: string | null): React.ReactNode[] {
  if (!text) return [];
  const parts = text.split(/(@\S+)/g);
  return parts.map((p, i) =>
    p.startsWith('@')
      ? <span key={i} className="mention mono">{p}</span>
      : <React.Fragment key={i}>{p}</React.Fragment>
  );
}

// ─── Role switcher ─────────────────────────────────────────
export type Role = 'host' | 'participant' | 'director';

export function RoleSwitcher({
  role, onChange, compact,
}: { role: Role; onChange: (r: Role) => void; compact?: boolean }) {
  const opts: { id: Role; label: string; sub: string }[] = [
    { id: 'host',        label: '主持人', sub: 'HOST' },
    { id: 'participant', label: '参会者', sub: 'PART' },
    { id: 'director',    label: '导演',   sub: 'DIR'  },
  ];
  return (
    <div style={{
      display: 'inline-flex', border: '1px solid var(--line-2)',
      borderRadius: 4, overflow: 'hidden',
      fontFamily: 'var(--mono)', fontSize: 10,
    }}>
      {opts.map(o => (
        <button key={o.id} onClick={() => onChange(o.id)}
          style={{
            appearance: 'none', border: 0,
            padding: compact ? '4px 8px' : '5px 10px',
            background: role === o.id ? 'var(--bg-3)' : 'transparent',
            color: role === o.id ? 'var(--fg-0)' : 'var(--fg-2)',
            cursor: 'pointer',
            borderRight: '1px solid var(--line-1)',
            fontFamily: 'inherit', fontSize: 'inherit',
            letterSpacing: '0.04em',
          }}>
          {compact ? o.sub : (
            <span>
              <span style={{ color: role === o.id ? 'var(--live)' : 'var(--fg-3)' }}>[{o.sub}]</span>{' '}
              {o.label}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ─── Tiny icon set (stroke only) ───────────────────────────
export const I: Record<string, React.ReactElement> = {
  pause: <svg width="11" height="11" viewBox="0 0 16 16"><path d="M5 3v10M11 3v10" stroke="currentColor" strokeWidth="1.4" fill="none" /></svg>,
  play:  <svg width="11" height="11" viewBox="0 0 16 16"><path d="M4 3l9 5-9 5z" fill="currentColor" /></svg>,
  rewind:<svg width="11" height="11" viewBox="0 0 16 16"><path d="M8 3L3 8l5 5M14 3L9 8l5 5" stroke="currentColor" strokeWidth="1.4" fill="none" /></svg>,
  fork:  <svg width="11" height="11" viewBox="0 0 16 16"><path d="M5 3v4a2 2 0 002 2h2a2 2 0 002-2V3M8 9v4" stroke="currentColor" strokeWidth="1.4" fill="none" /><circle cx="5" cy="3" r="1.4" fill="currentColor" /><circle cx="11" cy="3" r="1.4" fill="currentColor" /><circle cx="8" cy="13" r="1.4" fill="currentColor" /></svg>,
  send:  <svg width="11" height="11" viewBox="0 0 16 16"><path d="M2 8l12-5-4 12-3-5z" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinejoin="round" /></svg>,
  mic:   <svg width="11" height="11" viewBox="0 0 16 16"><rect x="6" y="2" width="4" height="8" rx="2" stroke="currentColor" strokeWidth="1.4" fill="none" /><path d="M3 8a5 5 0 0010 0M8 13v2" stroke="currentColor" strokeWidth="1.4" fill="none" /></svg>,
  vote:  <svg width="11" height="11" viewBox="0 0 16 16"><path d="M3 8l3 3 7-7" stroke="currentColor" strokeWidth="1.6" fill="none" /></svg>,
  x:     <svg width="11" height="11" viewBox="0 0 16 16"><path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.4" /></svg>,
  plus:  <svg width="11" height="11" viewBox="0 0 16 16"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.4" /></svg>,
  chevR: <svg width="9" height="9" viewBox="0 0 16 16"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.4" fill="none" /></svg>,
  dots:  <svg width="11" height="11" viewBox="0 0 16 16"><circle cx="3" cy="8" r="1.2" fill="currentColor" /><circle cx="8" cy="8" r="1.2" fill="currentColor" /><circle cx="13" cy="8" r="1.2" fill="currentColor" /></svg>,
  search:<svg width="11" height="11" viewBox="0 0 16 16"><circle cx="7" cy="7" r="4" stroke="currentColor" strokeWidth="1.4" fill="none" /><path d="M10 10l4 4" stroke="currentColor" strokeWidth="1.4" /></svg>,
};

// ─── Toggle (on/off pill) ─────────────────────────────────
export function Toggle({ on, onChange }: { on: boolean; onChange?: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange && onChange(!on)}
      style={{
        appearance: 'none', border: 0, padding: 0, cursor: 'pointer',
        width: 32, height: 16, borderRadius: 100,
        background: on ? 'var(--live)' : 'var(--bg-3)',
        position: 'relative', transition: 'background .15s',
      }}>
      <span style={{
        position: 'absolute', top: 2, left: on ? 18 : 2,
        width: 12, height: 12, borderRadius: '50%',
        background: on ? 'var(--bg-0)' : 'var(--fg-2)',
        transition: 'left .15s',
      }} />
    </button>
  );
}

// ─── Segmented radio ──────────────────────────────────────
export function SegRadio<T extends string>({
  value, options, onChange,
}: {
  value: T;
  options: { id: T; label: string; color?: string }[];
  onChange?: (v: T) => void;
}) {
  return (
    <div className="flex" style={{
      border: '1px solid var(--line-2)', borderRadius: 4, padding: 2,
      fontFamily: 'var(--mono)', fontSize: 11, gap: 2,
    }}>
      {options.map(o => (
        <button key={o.id}
          onClick={() => onChange && onChange(o.id)}
          style={{
            flex: 1, appearance: 'none', border: 0, borderRadius: 3,
            padding: '6px 10px', cursor: 'pointer',
            background: value === o.id ? 'var(--bg-3)' : 'transparent',
            color: value === o.id ? (o.color || 'var(--fg-0)') : 'var(--fg-2)',
            fontFamily: 'inherit', fontSize: 'inherit',
            fontWeight: value === o.id ? 600 : 400,
            letterSpacing: '0.04em',
          }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ─── ASCII separator helper ─────────────────────────────────
export function AsciiSep({ label, total = 60 }: { label?: string; total?: number }) {
  const inner = label ? ` ${label} ` : '';
  const padTotal = Math.max(0, total - inner.length);
  const left = '─'.repeat(Math.floor(padTotal / 2));
  const right = '─'.repeat(Math.ceil(padTotal / 2));
  return <div className="ascii-sep" style={{ padding: '12px 14px' }}>{left}{inner}{right}</div>;
}
